import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import { arrayOf, bool, func } from 'prop-types';
import React from 'react';
import { Alert, Button, Col, Row } from 'reactstrap';

import AttachmentsGrid from '../../../attachments/AttachmentsGrid';
import SignedCurrencyFormat from '../../../generic/SignedCurrencyFormat';
import SubmittableForm from '../../../generic/SubmittableForm';
import TimestampInfo from '../../../generic/TimestampInfo';
import { calculateTotalSum } from '../../../invoices/utils';
import { invoiceTypeEnums } from '../../../opportunities/constants';
import {
  attachmentShape,
  bookingEntryShape,
  detailedInvoiceShape,
  productShape,
} from '../../../prop-types';
import api from '../../../REST/api';
import withResolved from '../../../REST/with-resolved';
import ListBookingEntries from '../../booking-entries/ListBookingEntries';
import { allStatusesEnum } from '../../constants';
import InvoiceFields from '../InvoiceFields';

class InvoiceForm extends React.Component {
  state = {
    allEntries: [],
  };

  componentDidMount() {
    const { model, bookingEntries } = this.props;
    const allEntries = isEmpty(model.bookingEntries)
      ? bookingEntries
      : model.bookingEntries.concat(bookingEntries);

    this.setState({ allEntries });
  }

  handleAddEntry = entry => {
    this.add(this.props.model.bookingEntries.concat(entry));
  };

  handleRemoveEntry = entry => {
    this.props.onChange({
      target: {
        value: this.props.model.bookingEntries.filter(ea => ea !== entry),
        name: 'bookingEntries',
      },
    });
  };

  handleAddAllEntries(entries) {
    this.add(this.props.model.bookingEntries.concat(entries));
  }

  add(entries) {
    const { onChange } = this.props;
    onChange({
      target: {
        value: sortBy(entries, ['created', 'serviceStartDate']),
        name: 'bookingEntries',
      },
    });
  }

  render() {
    const {
      model,
      products,
      onChange,
      onSubmit,
      newModel,
      readOnly,
      showOpportunity,
      attachments,
    } = this.props;
    const {
      created,
      lastModified,
      createdBy,
      lastModifiedBy,
      purchaseOrderNumber,
      status,
      type,
    } = model;

    const [invoiceBookingEntries, floatingBookingEntries] = partition(
      this.state.allEntries,
      entry => model.bookingEntries.includes(entry),
    );

    const isNonEmptyPONumber =
      purchaseOrderNumber !== '' && /^\d*$/.test(purchaseOrderNumber);

    const shouldDisplayWarning =
      status === allStatusesEnum.DRAFT.value &&
      type === invoiceTypeEnums.AUTOMATIC_MONTHLY &&
      !isNonEmptyPONumber;

    return (
      <SubmittableForm
        onSubmit={readOnly ? undefined : onSubmit}
        id="invoice-form"
      >
        {shouldDisplayWarning && (
          <Alert color="primary">
            This invoice account requires mandatory PO number, but the invoice
            PO number is missing/wrong. Please fix it before sending it for
            Accounting&apos;s review.
          </Alert>
        )}
        <InvoiceFields
          onChange={onChange}
          readOnly={readOnly}
          model={model}
          showOpportunity={showOpportunity}
        />
        <Row className="mt-1">
          <h4 className="ml-3 mb-3">Attachment</h4>
        </Row>
        <Row className="mb-3 ml-3">
          {attachments.length > 0 ? (
            <AttachmentsGrid files={attachments} noDeleteOption />
          ) : (
            'No Attachments attached'
          )}
        </Row>
        <Row className="mt-5">
          <Col>
            <ListBookingEntries
              products={products}
              id="qa-invoiced-booking-entries"
              className="border-bottom mb-2"
              showHeader
              key={invoiceBookingEntries.length}
              data={invoiceBookingEntries}
              invoice={model}
              onRemove={readOnly ? undefined : this.handleRemoveEntry}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm={10} />
          <Col sm={2} className="px-0">
            <SignedCurrencyFormat
              name="invoiceAmount"
              value={calculateTotalSum(invoiceBookingEntries)}
            />
            <div className="small text-muted">Invoice Amount</div>
          </Col>
        </Row>
        {!readOnly && floatingBookingEntries.length > 0 && (
          <Row className="mt-3">
            <Col>
              <h5 className="d-flex justify-content-between">
                Open Entries
                <Button
                  onClick={() => {
                    return this.handleAddAllEntries(floatingBookingEntries);
                  }}
                >
                  Add all
                </Button>
              </h5>
              <ListBookingEntries
                products={products}
                id="qa-floating-booking-entries"
                key={floatingBookingEntries.length}
                data={floatingBookingEntries}
                onAdd={this.handleAddEntry}
              />
            </Col>
          </Row>
        )}
        {!newModel && (
          <Row className="small mb-4">
            <Col>
              <TimestampInfo
                timestamp={created}
                actionName="created"
                author={createdBy}
                displayAttribute="inline"
              />
              <TimestampInfo
                timestamp={lastModified}
                actionName="lastModified"
                author={lastModifiedBy}
                displayAttribute="inline"
              />
            </Col>
          </Row>
        )}
      </SubmittableForm>
    );
  }
}

InvoiceForm.propTypes = {
  model: detailedInvoiceShape.isRequired,
  bookingEntries: arrayOf(bookingEntryShape.isRequired),
  products: arrayOf(productShape.isRequired).isRequired,
  onChange: func,
  onSubmit: func,
  newModel: bool,
  readOnly: bool,
  showOpportunity: bool,
  attachments: arrayOf(attachmentShape).isRequired,
};

InvoiceForm.defaultProps = {
  bookingEntries: [],
  onChange: undefined,
  onSubmit: undefined,
  newModel: false,
  readOnly: false,
  showOpportunity: false,
};

export default withResolved({
  query: api.getAttachments,
  deriveQueryArgs: ({ model }) => ({
    search: {
      invoiceId: model.id,
    },
  }),
  as: 'attachments',
})(InvoiceForm);
